/**
 * Current sale or rental offerings around the property on a Google map: price
 * pins (ticked when they contributed to the saved assessment), stacks and count
 * bubbles where labels would overlap, the assessment radius and the subject
 * house. Hovering previews a listing without moving the map; a click pins it.
 * Filters explore the cohort without changing the assessment.
 */
import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react'
import { createPortal, flushSync } from 'react-dom'
import { IconMapPin } from '@tabler/icons-react'
import { hasMapPosition, useMarketOfferings, type MarketOfferingFilters, type MarketOfferings, type MarketTransactionType } from '../../../api/marketOfferings'
import { isDarkTheme, loadGoogleMaps, themeToken, type GoogleMapsLibraries } from '../../../services/googleMaps'
import { MapPopupOverlay } from '../../../services/mapPopup'
import { Alert, Button, PickerRow, type Option } from '../../../ui/kit'
import { label as titleCase, neutral } from './format'
import { mountOfferingMarkers, type OfferingMarkers, type PopupView } from './offeringMarkers'
import { OfferingList, OfferingPopup } from './OfferingPopup'
import { Help, Note, Panel } from './ui'

function options(values: string[], label: (value: string) => string = titleCase): Option[] {
  return [...new Set(values)].filter(Boolean).map(value => ({ value, label: label(value) }))
}

/** Generic bungalow evidence is assumed detached unless an attachment is specified. */
function propertyType(value: string | null | undefined): string | null {
  return value === 'bungalow' ? 'detached_bungalow' : value ?? null
}

function tenureOptions(defaultTenure: string | null | undefined, selectedTenure: string | null | undefined): Option[] {
  const canonical = (value: string) => value.trim().toUpperCase().replace(/[ -]/g, '_')
  const codes = [...new Set(['FREEHOLD', 'LEASEHOLD', 'SHARE_OF_FREEHOLD', 'NON_TRADITIONAL', ...[defaultTenure, selectedTenure].filter((value): value is string => !!value).map(canonical)])]
  return codes.map(code => ({ value: [selectedTenure, defaultTenure].find(value => value && canonical(value) === code) ?? code, label: titleCase(code.toLowerCase()) }))
}

/** Prefer nearby detail, but keep the house and a real offering in the first view.
 * The initial fitting ends at user input; later navigation belongs to the user. */
function initialViewport({ map, element, center, radiusBounds, positions, createBounds }: {
  map: google.maps.Map; element: HTMLElement; center: google.maps.LatLngLiteral; radiusBounds: google.maps.LatLngBounds | null
  positions: google.maps.LatLngLiteral[]; createBounds: () => google.maps.LatLngBounds
}): () => void {
  const radians = Math.PI / 180
  const separation = (p: google.maps.LatLngLiteral) => Math.sin((p.lat - center.lat) * radians / 2) ** 2 + Math.cos(center.lat * radians) * Math.cos(p.lat * radians) * Math.sin((p.lng - center.lng) * radians / 2) ** 2
  const nearest = positions.reduce<google.maps.LatLngLiteral | null>((best, p) => !best || separation(p) < separation(best) ? p : best, null)
  let pending: google.maps.MapsEventListener | undefined
  let cancelled = false
  const cancel = () => { cancelled = true; pending?.remove(); pending = undefined }
  const afterIdle = (callback: () => void) => { pending = google.maps.event.addListenerOnce(map, 'idle', () => { pending = undefined; if (!cancelled) callback() }) }
  const keepNearestVisible = () => {
    if (!nearest) return
    const bounds = map.getBounds()
    if (!bounds?.contains(center) || !bounds.contains(nearest)) map.fitBounds(createBounds().extend(center).extend(nearest), 32)
  }
  const preferNearby = () => {
    const zoom = map.getZoom() ?? 14
    const preferred = radiusBounds ? Math.min(zoom + 2, 16) : 16
    const previous = map.getCenter()
    if (zoom === preferred && previous?.lat() === center.lat && previous.lng() === center.lng) keepNearestVisible()
    else { afterIdle(keepNearestVisible); map.setCenter(center); map.setZoom(preferred) }
  }
  const drag = map.addListener('dragstart', cancel)
  element.addEventListener('pointerdown', cancel, { capture: true, passive: true })
  element.addEventListener('wheel', cancel, { capture: true, passive: true })
  element.addEventListener('keydown', cancel, true)
  if (radiusBounds) { afterIdle(preferNearby); map.fitBounds(radiusBounds, 24) } else preferNearby()
  return () => {
    cancel(); drag.remove()
    element.removeEventListener('pointerdown', cancel, true)
    element.removeEventListener('wheel', cancel, true)
    element.removeEventListener('keydown', cancel, true)
  }
}

function houseGlyph(): SVGSVGElement {
  const house = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
  house.setAttribute('viewBox', '0 0 24 24'); house.setAttribute('aria-hidden', 'true')
  for (const d of ['M3 12l9-9 9 9', 'M5 10v9a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-9', 'M9 21v-8h6v8']) {
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path'); path.setAttribute('d', d); house.append(path)
  }
  return house
}

function OfferingMap({ data, transactionType, busy }: { data: MarketOfferings; transactionType: MarketTransactionType; busy: boolean }) {
  const node = useRef<HTMLDivElement | null>(null)
  const map = useRef<google.maps.Map | null>(null)
  const libs = useRef<GoogleMapsLibraries | null>(null)
  const overlay = useRef<MapPopupOverlay | null>(null)
  const controller = useRef<OfferingMarkers | null>(null)
  const busyRef = useRef(busy)
  const [ready, setReady] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const [visible, setVisible] = useState(false)
  const [shell, setShell] = useState<HTMLDivElement | null>(null)
  const [popup, setPopup] = useState<PopupView | null>(null)
  const popupId = `dil-pop-${useId()}`
  const located = hasMapPosition(data.subject)
  const close = useCallback(() => controller.current?.close(true), [])

  // Place the shell once its new content is in the DOM, before paint and before the card takes focus.
  useLayoutEffect(() => { overlay.current?.place() }, [popup, shell, busy])
  // No popup while placeholder data is shown: the cohort is about to change.
  useEffect(() => { busyRef.current = busy; if (busy) controller.current?.close(false) }, [busy])

  // Load the map only when the panel scrolls near the viewport.
  useEffect(() => {
    const element = node.current
    if (!element) return
    if (typeof IntersectionObserver === 'undefined') { setVisible(true); return }
    const observer = new IntersectionObserver(entries => { if (entries.some(entry => entry.isIntersecting)) { setVisible(true); observer.disconnect() } }, { rootMargin: '150px' })
    observer.observe(element)
    return () => observer.disconnect()
  }, [located])

  // One map per mounted subject; cohort changes update its markers below.
  useEffect(() => {
    if (!visible || !node.current || !located) return
    const target = node.current
    let cancelled = false
    let resize: ResizeObserver | null = null
    setError(null)
    void loadGoogleMaps().then(loaded => {
      if (cancelled) return
      libs.current = loaded
      map.current = new loaded.maps.Map(target, {
        center: { lat: data.subject.latitude!, lng: data.subject.longitude! }, zoom: 14, mapId: loaded.mapId,
        colorScheme: isDarkTheme() ? 'DARK' : 'LIGHT', backgroundColor: themeToken('--muted'),
        mapTypeControl: false, streetViewControl: false, fullscreenControl: true, gestureHandling: 'cooperative', clickableIcons: false,
      })
      resize = new ResizeObserver(() => { if (map.current) google.maps.event.trigger(map.current, 'resize') })
      resize.observe(target)
      setReady(value => value + 1)
    }).catch((cause: unknown) => { if (!cancelled) setError(cause instanceof Error ? cause.message : 'Google Maps could not be loaded') })
    return () => {
      cancelled = true; resize?.disconnect()
      if (map.current) google.maps.event.clearInstanceListeners(map.current)
      map.current = null; libs.current = null; target.replaceChildren()
    }
  }, [visible, data.subject.propertyId, located]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const instance = map.current, loaded = libs.current, element = node.current
    if (!instance || !loaded || !element || !located) return
    setPopup(null)
    const center = { lat: data.subject.latitude!, lng: data.subject.longitude! }
    const subjectContent = document.createElement('span')
    subjectContent.className = 'map-pin is-subject'
    subjectContent.setAttribute('role', 'img')
    subjectContent.setAttribute('aria-label', data.subject.address || 'Property location')
    subjectContent.append(houseGlyph())
    const subject = new loaded.marker.AdvancedMarkerElement({ map: instance, position: center, content: subjectContent, title: data.subject.address || 'Property location', zIndex: 2000, gmpClickable: false })
    // The subject is a location label; offerings underneath keep mouse and focus input.
    subject.style.pointerEvents = 'none'; subject.tabIndex = -1
    let stopInitialView = () => {}
    // gap 10 leaves the stem tip 3px from the pin; arrowInset 20 keeps the stem clear of the card corner.
    // `avoid` backs up the detected Google controls should their class names change: this map's
    // fullscreen button (top right) and the logo and attribution strip along the bottom.
    const popupOverlay = new MapPopupOverlay(loaded.maps, instance, {
      id: popupId, className: 'dil-pop', placement: { gap: 10, margin: 8, arrowInset: 20 },
      avoid: ({ width, height }) => [{ x: width - 50, y: 0, width: 50, height: 50 }, { x: 0, y: height - 16, width, height: 16 }],
    })
    overlay.current = popupOverlay
    setShell(popupOverlay.shell)
    const markers = mountOfferingMarkers({
      loaded, map: instance, container: element, rows: data.rows, transactionType, popup: popupOverlay,
      show: (view, sync) => { if (sync) flushSync(() => setPopup(view)); else setPopup(view) },
      busy: () => busyRef.current, onOpen: () => stopInitialView(),
    })
    controller.current = markers
    const circle = new loaded.maps.Circle({ map: instance, center, radius: data.radiusUsedMiles * 1609.344, fillOpacity: 0, strokeColor: themeToken('--ink-muted'), strokeOpacity: 0.6, strokeWeight: 1, clickable: false })
    stopInitialView = initialViewport({ map: instance, element, center, radiusBounds: data.radiusUsedMiles > 0 ? circle.getBounds() ?? null : null, positions: data.rows.filter(hasMapPosition).map(row => ({ lat: row.latitude!, lng: row.longitude! })), createBounds: () => new loaded.core.LatLngBounds() })
    return () => {
      stopInitialView()
      markers.dispose(); controller.current = null
      popupOverlay.dispose(); overlay.current = null; setShell(null)
      subject.map = null; circle.setMap(null)
    }
  }, [ready, data, transactionType, located, popupId])

  if (!located) return <Note>The property location is unavailable. Listings cannot be placed around this address yet.</Note>
  return (
    <div className="dil-map" data-loading={busy || undefined}>
      {/* tabIndex -1: focus can land on the map itself, e.g. after "Zoom in" closes a list. */}
      <div ref={node} className="map-canvas" role="region" tabIndex={-1} aria-label={`Nearby ${transactionType === 'sale' ? 'sale' : 'rental'} offerings map`} aria-busy={busy || (!ready && !error) || undefined} />
      {error ? <div className="map-error"><Alert warn tight title="Map unavailable">{neutral(error)}</Alert></div> : busy || !ready ? <div className="dil-map-loading" role="status">{busy ? 'Updating listings…' : 'Loading map…'}</div> : null}
      {popup && shell && !busy ? createPortal(popup.kind === 'group'
        ? <OfferingList key={popup.key} offerings={popup.offerings} transactionType={transactionType} zoom={popup.zoom} onClose={close} />
        : <OfferingPopup key={popup.offering.id} offering={popup.offering} transactionType={transactionType} pinned={popup.pinned} onClose={close} />, shell) : null}
    </div>
  )
}

export function MarketOfferingsMap({ propertyId, transactionType }: { propertyId: string; transactionType: MarketTransactionType }) {
  const [overrides, setOverrides] = useState<MarketOfferingFilters | null>(null)
  const query = useMarketOfferings(propertyId, transactionType, overrides)
  const data = query.data
  const filters = overrides ?? data?.filters
  const defaults = data?.defaults
  const title = transactionType === 'sale' ? 'Current sale offerings' : 'Current rental offerings'
  const change = (patch: Partial<MarketOfferingFilters>) => { if (filters) setOverrides({ bedrooms: filters.bedrooms, propertyType: propertyType(filters.propertyType), tenure: filters.tenure, radiusMiles: filters.radiusMiles, ...patch }) }
  const propertyTypes = options(['detached', 'semi_detached', 'terraced', 'end_terrace', 'detached_bungalow', 'semi_detached_bungalow', 'terraced_bungalow', 'flat', 'maisonette', 'studio', 'house', propertyType(defaults?.propertyType) ?? '', propertyType(filters?.propertyType) ?? ''])
  const tenures = tenureOptions(defaults?.tenure, filters?.tenure)
  const radii = options(['0', '0.25', '0.5', '1', '2', '3', '5', String(defaults?.radiusMiles ?? 1), String(filters?.radiusMiles ?? 1)], value => Number(value) === 0 ? 'Exact postcode' : `${value} miles`).sort((a, b) => Number(a.value) - Number(b.value))
  const bedrooms = [{ value: '', label: 'All bedrooms' }, ...options(['0', '1', '2', '3', '4', '5', '6', '7', '8', ...(filters?.bedrooms == null ? [] : [String(filters.bedrooms)])], value => value === '0' ? 'Studio' : `${value} bedrooms`)]
  const unplaced = data?.rows.filter(row => !hasMapPosition(row)).length ?? 0
  const shown = data?.rows.filter(row => row.usedInAssessment && hasMapPosition(row)).length ?? 0
  const pick = (value: string[] | string | null) => (typeof value === 'string' ? value : null)
  return (
    <Panel title={title} icon={<IconMapPin className="ic" aria-hidden="true" />} className="dil-offerings"
      rating={<Help label={title} help={{
        what: 'Retained nearby property adverts and the saved contributors to the assessment.',
        why: 'Lets you inspect the properties behind local sale or rent comparisons and explore a broader cohort.',
        read: <><p>Ticked markers identify the saved assessment contributors. The starting filters use this property's facts and assessment radius. Changing filters explores listings without changing its assessment. Hover over a price to preview a listing; click or press Enter to pin it. Listings whose labels would overlap are stacked; larger groups show a count that opens a list. Shared postcode positions cannot be separated by zooming.</p><p>Prices are advertised amounts. Coordinates may represent postcode centres. Saved contributors remain available if current details have changed. The map shows up to 200 listings{data?.truncated ? `; ${data.returnedCount} of ${data.totalCount} matching and saved listings are returned` : ''}.</p>{unplaced > 0 ? <p>{unplaced} returned listings have no map coordinates.</p> : null}{data?.warnings.map(warning => <p key={warning}>{warning}</p>)}</>,
      }} />}
      actions={<>{data && !query.isError ? <span className="dil-counts" role="status">{data.currentMatchingCount.toLocaleString('en-GB')} matches · {shown}/{data.assessmentCount} comparables shown</span> : null}{overrides ? <Button small onClick={() => setOverrides(null)}>Reset</Button> : null}</>}>
      {filters && defaults ? (
        <div className="dil-filters" aria-label={`${title} filters`}>
          <PickerRow label="Bedrooms" options={bedrooms} value={filters.bedrooms === null ? '' : String(filters.bedrooms)} defaultValue={defaults.bedrooms === null ? '' : String(defaults.bedrooms)} clearable={false} onChange={value => { const next = pick(value); if (next !== null) change({ bedrooms: next === '' ? null : Number(next) }) }} />
          <PickerRow label="House type" options={[{ value: '', label: 'All types' }, ...propertyTypes]} value={propertyType(filters.propertyType) ?? ''} defaultValue={propertyType(defaults.propertyType) ?? ''} clearable={false} onChange={value => { const next = pick(value); if (next !== null) change({ propertyType: next || null }) }} />
          <PickerRow label="Tenure" options={[{ value: '', label: 'All tenures' }, ...tenures]} value={filters.tenure ?? ''} defaultValue={defaults.tenure ?? ''} clearable={false} onChange={value => { const next = pick(value); if (next !== null) change({ tenure: next || null }) }} />
          <PickerRow label="Radius" options={radii} value={String(filters.radiusMiles)} defaultValue={String(defaults.radiusMiles)} clearable={false} onChange={value => { const next = pick(value); if (next !== null) change({ radiusMiles: Number(next) }) }} />
        </div>
      ) : null}
      {query.isError ? <Alert tight title="Listings unavailable">{neutral(query.error.message)}</Alert> : null}
      {data && !query.isError ? <>
        <OfferingMap data={data} transactionType={transactionType} busy={query.isPlaceholderData} />
        {data.totalCount === 0 ? <Note>No matching retained listings. Broaden a filter or radius to explore the area.</Note> : null}
      </> : query.isPending ? <Note>Loading nearby listings…</Note> : null}
    </Panel>
  )
}
